import React, { useState, useEffect } from 'react';
import RTButton from '../common/RTButton';
import RTSpinner from '../common/RTSpinner';
import Table from '../common/Table';
import { FaEdit, FaTrash } from 'react-icons/fa';
import tenantStorage from '../../utils/tenantStorage';
import AddInvoiceItemType from './AddInvoiceItemType';
import { apiGet } from '../../api/apiClient';
import apiClient from '../../api/apiClient';
import { toastSuccess, toastError, toastConfirm } from '../common/toast';
import '../common/rtCommon.css';
import './InvoiceItemTypesList.css';

const InvoiceItemTypesList = () => {
    const [invoiceItemTypes, setInvoiceItemTypes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [deleting, setDeleting] = useState(false);
    const [error, setError] = useState('');
    const [showAddEdit, setShowAddEdit] = useState(false);
    const [editType, setEditType] = useState(null);

    useEffect(() => {
        fetchInvoiceItemTypes();
    }, []);

    const fetchInvoiceItemTypes = async () => {
        try {
            setLoading(true);
            const response = await apiGet('/invoicing/invoice-item-types');
            // Access response.data and ensure it's an array
            const data = response?.data || response || [];
            const typesArray = Array.isArray(data) ? data : [];
            
            // Filter to show only active types (case-insensitive)
            const activeTypes = typesArray.filter(type => 
                type.status && type.status.toLowerCase() === 'active'
            );
            
            setInvoiceItemTypes(activeTypes);
            setError('');
        } catch (err) {
            console.error('Error fetching invoice item types:', err);
            setError('Failed to load invoice item types');
            toastError('Failed to load invoice item types');
            setInvoiceItemTypes([]); // Set empty array on error
        } finally {
            setLoading(false);
        }
    };

    const handleCreate = () => {
        setEditType(null);
        setShowAddEdit(true);
    };

    const handleEdit = (itemType) => {
        setEditType(itemType);
        setShowAddEdit(true);
    };

    const handleDelete = async (typeId, typeName) => {
        const confirmed = await toastConfirm(true, `Delete invoice item type "${typeName}"?`);
        if (!confirmed) return;

        try {
            setDeleting(true);
            const token = tenantStorage.getItem('accessToken');
            
            const response = await fetch(`${process.env.REACT_APP_API_URL}/invoicing/invoice-item-types/${typeId}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.detail || 'Failed to delete invoice item type');
            }

            const result = await response.json();
            fetchInvoiceItemTypes();
            toastSuccess(result.message || 'Invoice item type deleted successfully');
        } catch (err) {
            console.error('Error deleting invoice item type:', err);
            const errorMessage = err.message || 'Failed to delete invoice item type';
            setError(errorMessage);
            toastError(errorMessage);
        } finally {
            setDeleting(false);
        }
    };

    const columns = [
        {
            Header: 'Name',
            accessor: 'name',
            sortable: true
        },
        {
            Header: 'Description',
            accessor: 'description',
            sortable: true,
            Cell: ({ value }) => value || '-'
        },
        {
            Header: 'Actions',
            accessor: 'actions',
            disableSortBy: true,
            Cell: ({ row }) => {
                const itemType = row.original || row.values || row;
                const typeId = itemType.id;
                const typeName = itemType.name;
                
                return (
                    <div className="action-buttons" style={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
                        <FaEdit 
                            style={{ cursor: 'pointer', color: '#153C7F', fontSize: '16px' }} 
                            onClick={() => handleEdit(itemType)} 
                            title="Edit" 
                        />
                        <FaTrash 
                            style={{ cursor: 'pointer', color: '#d32f2f', fontSize: '16px' }} 
                            onClick={() => handleDelete(typeId, typeName)} 
                            title="Delete" 
                        />
                    </div>
                );
            }
        }
    ];

    if (showAddEdit) {
        return <AddInvoiceItemType type={editType} onClose={() => { setShowAddEdit(false); fetchInvoiceItemTypes(); }} />;
    }

    if (loading) {
        return <RTSpinner />;
    }

    return (
        <div style={{ background: 'transparent' }}>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '20px' }}>
                <RTButton requiresWrite
                    variant="primary"
                    onClick={handleCreate}
                >
                    Add Invoice Item Type
                </RTButton>
            </div>

            {error && (
                <div className="error-message">
                    {error}
                </div>
            )}

            <div className="table-container">
                <Table
                    data={invoiceItemTypes}
                    columns={columns}
                    exportable={false}
                    showSearch={false}
                    showTotalCount={true}
                    className="invoice-item-types-table"
                />
            </div>

            {/* Delete Loading Overlay */}
            {deleting && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 9999
                }}>
                    <RTSpinner size="medium" showMessage={true} message="Deleting invoice item type..." />
                </div>
            )}
        </div>
    );
};

export default InvoiceItemTypesList;
